import type { CustomMetricUnit, MetricRow } from "@aco/core";

const currencyFormatters = new Map<string, Intl.NumberFormat>();

function currencyFormatter(currency: string): Intl.NumberFormat {
  const cached = currencyFormatters.get(currency);
  if (cached) return cached;
  const formatter = new Intl.NumberFormat("nb-NO", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  currencyFormatters.set(currency, formatter);
  return formatter;
}

/**
 * Round to the same precision used everywhere a metric value is displayed (0
 * decimals). CSV export and the on-screen grid both call this so a cell never
 * shows one number on screen and a different one in the export.
 */
export function roundMetricValue(value: number): number {
  return Math.round(value);
}

/**
 * Currency-aware formatting for on-screen display. Keeps nb-NO number-style
 * grouping/decimals regardless of currency; only the currency symbol/code
 * varies. One org = one currency (see the mergeTotals comment in
 * grid-aggregation.ts) — per-account currency is out of scope.
 */
export function formatMetricValue(
  value: number,
  type: MetricRow["type"],
  currency: string,
): string {
  const rounded = roundMetricValue(value);
  if (type === "currency" || type === "currency_per") {
    try {
      return currencyFormatter(currency).format(rounded);
    } catch {
      // Malformed/unknown currency code (e.g. bad manual edit) — fall back to
      // a plain number with the raw code rather than crashing the grid.
      return `${rounded.toLocaleString("nb-NO", { minimumFractionDigits: 0, maximumFractionDigits: 0 })} ${currency}`;
    }
  }
  return rounded.toLocaleString("nb-NO", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

/**
 * Manual data (issue 16) formatting for display. Unlike platform metrics, a
 * Custom metric keeps the precision a person entered: decimal and percentage
 * units show up to two fraction digits, currency reuses the workspace currency
 * formatter, and count renders as a grouped integer. A trailing `%` is appended
 * for percentage units — the stored number is the percentage, not a ratio.
 */
export function formatManualValue(value: number, unit: CustomMetricUnit, currency: string): string {
  switch (unit) {
    case "currency":
      return formatMetricValue(value, "currency", currency);
    case "percentage":
      return `${value.toLocaleString("nb-NO", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
    case "decimal":
      return value.toLocaleString("nb-NO", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    default:
      return value.toLocaleString("nb-NO", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  }
}
